import { useEffect, useRef } from 'react';

const possibilities = [
  {
    icon: 'ri-home-heart-line',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-700',
    title: 'Boutique Senior Living Residence',
    body: 'The single-level layout, secure gated grounds, tropical garden setting, and three-block walk to Matagalpa\'s Cathedral and Central Park make Montebrisa a natural fit for an intimate boutique senior living or assisted living community — where residents enjoy beauty, safety, and walkability in equal measure.',
  },
  {
    icon: 'ri-briefcase-4-line',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-700',
    title: 'Corporate Retreat Center',
    body: 'Far enough from the noise of daily operations to think clearly, close enough to Matagalpa\'s restaurants and culture to stay comfortable. Montebrisa\'s intimate rooms, custom hardwood furnishings, and lush surroundings create the ideal environment for executive strategy sessions, leadership programs, and team building retreats.',
  },
  {
    icon: 'ri-goblet-line',
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-700',
    title: 'Private Events Center',
    body: 'The tropical gardens, Art Déco architecture, and custom Nicaraguan hardwood interiors provide a stunning backdrop for private events — birthday celebrations, anniversaries, quinceañeras, gala dinners, and corporate functions unlike anything else in the region.',
  },
  {
    icon: 'ri-heart-line',
    iconBg: 'bg-pink-50',
    iconColor: 'text-pink-700',
    title: 'Wedding Venue',
    body: 'Imagine exchanging vows surrounded by 45 species of tropical plants, under the canopy of one of Matagalpa\'s most beautifully restored historic properties. Montebrisa offers an intimate, unforgettable setting for destination weddings that guests will talk about for decades.',
  },
  {
    icon: 'ri-mental-health-line',
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-700',
    title: 'Yoga & Wellness Retreat Center',
    body: 'The serene garden setting, natural surroundings, and proximity to the Apante Natural Reserve create an ideal sanctuary for yoga retreats, meditation programs, holistic wellness weekends, and mindfulness immersions.',
  },
  {
    icon: 'ri-restaurant-line',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-700',
    title: 'Culinary Tourism Destination & Cooking School',
    body: 'With a fully equipped kitchen, dining facilities, and a micro coffee farm of more than 450 coffee trees on the property, Montebrisa is a natural home for culinary tourism experiences, farm-to-table programming, and cooking schools celebrating Nicaragua\'s rich food culture.',
  },
  {
    icon: 'ri-camera-3-line',
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-700',
    title: 'Artist & Photography Residency',
    body: 'The Art Déco architecture, tropical gardens, and rich cultural surroundings of Matagalpa make Montebrisa an inspiring base for artist residencies, photography workshops, writing retreats, and creative immersion programs.',
  },
  {
    icon: 'ri-translate-2',
    iconBg: 'bg-sky-50',
    iconColor: 'text-sky-700',
    title: 'Language Immersion School',
    body: 'Nicaragua is one of Central America\'s most affordable and welcoming destinations for Spanish language learning. Montebrisa\'s residential layout, common areas, and walkable location make it an exceptional home for boutique Spanish immersion programs serving students from around the world.',
  },
  {
    icon: 'ri-computer-line',
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-700',
    title: 'Boutique Co-Living Space for Digital Nomads',
    body: 'With reliable infrastructure, high-speed internet capability, comfortable private rooms, shared common spaces, and a location that balances inspiration with affordability, Montebrisa is perfectly positioned as a premium co-living destination for remote workers and digital nomads seeking something beyond a standard coworking setup.',
  },
  {
    icon: 'ri-community-line',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-700',
    title: 'NGO or Nonprofit Regional Headquarters',
    body: 'The secure gated compound, multiple rooms, meeting spaces, vehicle, and central Matagalpa location make Montebrisa an ideal regional base for international nonprofit organizations, NGOs, or social enterprise operations working throughout Nicaragua and Central America.',
  },
  {
    icon: 'ri-graduation-cap-line',
    iconBg: 'bg-yellow-50',
    iconColor: 'text-yellow-700',
    title: 'Hospitality Training Academy',
    body: 'As a fully operational boutique B&B with award-winning systems already in place, Montebrisa could serve as a living classroom — training the next generation of Central American hospitality professionals in a real-world, working environment.',
  },
];

export default function BeyondBnBSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    cardRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="beyond-bnb" ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold rounded-full px-4 py-1.5 mb-6">
            <i className="ri-lightbulb-flash-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">Investment Possibilities</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-6">
            Beyond the B&B —<br /><em>What Montebrisa Could Become</em>
          </h2>
          <p className="font-sans text-base text-gray-600 leading-relaxed">
            Montebrisa&apos;s elegant single-level Art Déco architecture, lush tropical gardens, secure gated grounds, and fully turnkey infrastructure make it uniquely suited for a wide range of uses. Here are just a few possibilities.
          </p>
        </div>

        {/* Gold divider */}
        <div className="flex items-center justify-center gap-4 mb-16 reveal">
          <div className="h-px w-16 bg-gold/40" />
          <div className="w-1.5 h-1.5 rounded-full bg-gold" />
          <div className="h-px w-16 bg-gold/40" />
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {possibilities.map((item, idx) => (
            <div
              key={item.title}
              ref={el => { cardRefs.current[idx] = el; }}
              className="reveal group bg-cream rounded-2xl p-6 border border-cream-dark hover:-translate-y-1 transition-all duration-300"
              style={{ transitionDelay: `${(idx % 3) * 0.08}s` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-11 h-11 flex items-center justify-center ${item.iconBg} rounded-xl shrink-0`}>
                  <i className={`${item.icon} ${item.iconColor} text-lg`} />
                </div>
                <h3 className="font-sans font-semibold text-forest-dark text-sm leading-snug pt-1">
                  {item.title}
                </h3>
              </div>
              <p className="font-sans text-xs text-gray-500 leading-relaxed">
                {item.body}
              </p>
            </div>
          ))}
        </div>

        {/* Closing statement */}
        <div className="reveal">
          <div className="bg-forest rounded-2xl px-10 py-10 md:py-12 text-center max-w-4xl mx-auto">
            <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-6">
              <i className="ri-building-2-line text-white text-xl" />
            </div>
            <p className="font-serif text-white text-xl md:text-2xl leading-relaxed mb-2">
              The infrastructure is already here.
            </p>
            <p className="font-serif text-gold text-xl md:text-2xl leading-relaxed mb-6">
              The bones are extraordinary.
            </p>
            <p className="font-sans text-white/70 text-sm md:text-base leading-relaxed">
              The only question is what you&apos;ll build inside them.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
